import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Card,
  CardContent,
} from "../ui/card";
import {
  ScrollArea,
} from "../ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import VideoPlayer from '../VideoPlayer';
import { useAppConfig } from '../../hooks/useAppConfig';
import ColorPickerWithAlpha from '../ColorPickerWithAlpha';

export interface VideoCaption {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  // Style properties
  fontFamily?: string;
  fontSize?: number; // in pixels
  color?: string; // hex color
  backgroundColor?: string; // hex color with optional alpha
  position?: 'top' | 'center' | 'bottom'; // vertical position
}

interface VideoEditorProps {
  open: boolean;
  onClose: () => void;
  captions: VideoCaption[];
  onCaptionsChange: (captions: VideoCaption[]) => void;
  videoDuration: number;
  currentTime: number;
  onSeek: (time: number) => void;
  videoUrl: string; // URL of the video to preview
  startTime?: number; // Initial start time for trimming
  endTime?: number; // Initial end time for trimming
  onStartTimeChange?: (startTime: number) => void; // Callback when start time changes
  onEndTimeChange?: (endTime: number) => void; // Callback when end time changes
  enableTrimming?: boolean; // If true, show trimming slider
}

export default function VideoEditor({
  open,
  onClose,
  captions,
  onCaptionsChange,
  videoDuration,
  currentTime,
  onSeek,
  videoUrl,
  startTime: externalStartTime,
  endTime: externalEndTime,
  enableTrimming = false,
}: VideoEditorProps): JSX.Element {
  const [config] = useAppConfig();
  const [localCaptions, setLocalCaptions] = useState<VideoCaption[]>(captions);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [previewCurrentTime, setPreviewCurrentTime] = useState<number>(0);
  const [expandedCaptionId, setExpandedCaptionId] = useState<string | null>(null);
  const [selectedCaptionId, setSelectedCaptionId] = useState<string | null>(null);
  const [, setVideoDimensions] = useState<{ width: number; height: number } | null>(null);
  const [startTime, setStartTime] = useState<number>(externalStartTime ?? 0);
  const [endTime, setEndTime] = useState<number>(externalEndTime ?? videoDuration);
  const [crop, setCrop] = useState<{ x: number; y: number } | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);

  // Load crop and zoom from localStorage
  const loadCropAndZoom = useCallback(() => {
    const savedCropData = localStorage.getItem('videoCropData');
    if (savedCropData) {
      try {
        const parsed = JSON.parse(savedCropData);
        if (parsed.crop && parsed.zoom) {
          setCrop(parsed.crop);
          setZoom(parsed.zoom);
        }
      } catch (e) {
        console.warn('Error loading saved crop data:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (open) {
      setLocalCaptions(captions);
      setPreviewCurrentTime(currentTime);
      loadCropAndZoom();
      if (externalStartTime !== undefined) setStartTime(externalStartTime);
      if (externalEndTime !== undefined) setEndTime(externalEndTime);
    }
  }, [open, loadCropAndZoom, externalStartTime, externalEndTime, currentTime, captions]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'videoCropData') loadCropAndZoom();
    };
    window.addEventListener('storage', handleStorageChange);
    const handleCustomStorageChange = () => loadCropAndZoom();
    window.addEventListener('videoCropDataUpdated', handleCustomStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('videoCropDataUpdated', handleCustomStorageChange);
    };
  }, [loadCropAndZoom]);

  const handleSeekPreview = (time: number) => {
    setPreviewCurrentTime(time);
    onSeek(time);
  };

  const handleAddCaption = useCallback(() => {
    const timeToUse = previewCurrentTime || currentTime;
    const lastCaption = localCaptions.length > 0 ? localCaptions[localCaptions.length - 1] : null;
    const styleDefaults = lastCaption ? {
      fontFamily: lastCaption.fontFamily || config.captionDefaults.fontFamily,
      fontSize: lastCaption.fontSize || config.captionDefaults.fontSize,
      color: lastCaption.color || config.captionDefaults.color,
      backgroundColor: lastCaption.backgroundColor || config.captionDefaults.backgroundColor,
      position: lastCaption.position || config.captionDefaults.position,
    } : config.captionDefaults;
    
    const newCaption: VideoCaption = {
      id: `caption-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      text: '',
      startTime: timeToUse,
      endTime: Math.min(timeToUse + 3, videoDuration),
      ...styleDefaults,
    };
    setLocalCaptions([...localCaptions, newCaption]);
  }, [localCaptions, previewCurrentTime, currentTime, videoDuration, config]);

  const handleDeleteCaption = useCallback((id: string) => {
    setLocalCaptions(localCaptions.filter((c) => c.id !== id));
  }, [localCaptions]);

  const handleCaptionChange = useCallback((id: string, field: keyof VideoCaption, value: string | number) => {
    setLocalCaptions(
      localCaptions.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  }, [localCaptions]);

  const handleApply = useCallback(() => {
    const validCaptions = localCaptions.filter(
      (c) => c.text.trim() !== '' && c.startTime >= 0 && c.endTime > c.startTime && c.endTime <= videoDuration
    );
    onCaptionsChange(validCaptions);
    onClose();
  }, [localCaptions, onCaptionsChange, onClose, videoDuration]);

  const handleCancel = useCallback(() => {
    setLocalCaptions(captions);
    onClose();
  }, [captions, onClose]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleCancel()}>
      <DialogContent className="max-w-[100vw] w-full h-[100vh] m-0 p-0 border-none bg-muted overflow-hidden flex flex-col text-foreground">
        <DialogHeader className="p-4 border-b border-border">
          <DialogTitle className="text-foreground">Video Editor</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Video Preview - Left Side */}
          <div className="flex-1 min-w-0 p-4 flex flex-col">
            <VideoPlayer
              videoUrl={videoUrl}
              videoDuration={videoDuration}
              enableTrimming={enableTrimming}
              startTime={startTime}
              endTime={endTime}
              onStartTimeChange={enableTrimming ? setStartTime : undefined}
              onEndTimeChange={enableTrimming ? setEndTime : undefined}
              isPlaying={isPlaying}
              onPlayingChange={setIsPlaying}
              currentTime={previewCurrentTime}
              onCurrentTimeChange={setPreviewCurrentTime}
              onSeek={handleSeekPreview}
              captions={localCaptions}
              selectedCaptionId={selectedCaptionId || expandedCaptionId}
              onVideoDimensionsChange={setVideoDimensions}
              showSlider={true}
              showTrimmer={enableTrimming}
              crop={crop}
              zoom={zoom}
            />
          </div>

          {/* Captions List - Right Side */}
          <div className="w-[400px] border-l border-border bg-muted/50 flex flex-col min-h-0">
            <div className="p-4 border-b border-border flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">Captions</span>
              <Button size="sm" onClick={handleAddCaption} variant="outline" className="border-primary/50 text-primary hover:bg-primary/10">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {localCaptions.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-muted-foreground text-sm">No captions yet</p>
                  </div>
                ) : (
                  localCaptions.map((caption, index) => (
                    <Card 
                      key={caption.id} 
                      className={`border-border transition-colors cursor-pointer ${selectedCaptionId === caption.id ? 'bg-card border-primary/50' : 'bg-background/50'}`}
                      onClick={() => setSelectedCaptionId(caption.id)}
                    >
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">#{index + 1}</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={(e) => { e.stopPropagation(); handleDeleteCaption(caption.id); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-[10px] text-muted-foreground uppercase tracking-tight">Text</Label>
                          <Textarea 
                            className="bg-muted border-border text-foreground text-sm min-h-[60px] resize-none focus-visible:ring-primary/50"
                            value={caption.text}
                            onChange={(e) => handleCaptionChange(caption.id, 'text', e.target.value)}
                            placeholder="Caption text..."
                          />
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between text-[10px] text-muted-foreground font-mono">
                            <span>{formatTime(caption.startTime)}</span>
                            <span>{formatTime(caption.endTime)}</span>
                          </div>
                          <div className="px-1">
                            <Slider
                              range
                              min={0}
                              max={videoDuration}
                              value={[caption.startTime, caption.endTime]}
                              step={0.001}
                              onChange={(value) => {
                                if (Array.isArray(value) && value.length === 2) {
                                  const [newStart, newEnd] = value;
                                  handleCaptionChange(caption.id, 'startTime', newStart);
                                  handleCaptionChange(caption.id, 'endTime', newEnd);
                                }
                              }}
                              trackStyle={[{ backgroundColor: 'hsl(var(--primary))', height: 4 }]}
                              handleStyle={[
                                { borderColor: 'hsl(var(--primary))', backgroundColor: 'hsl(var(--primary))', boxShadow: 'none' },
                                { borderColor: 'hsl(var(--primary))', backgroundColor: 'hsl(var(--primary))', boxShadow: 'none' }
                              ]}
                              railStyle={{ backgroundColor: '#1e293b', height: 4 }}
                            />
                          </div>
                        </div>

                        <Collapsible 
                          open={expandedCaptionId === caption.id} 
                          onOpenChange={(v) => setExpandedCaptionId(v ? caption.id : null)}
                        >
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground hover:text-foreground h-7 px-2">
                              <span className="text-[10px] uppercase font-semibold">Style Settings</span>
                              {expandedCaptionId === caption.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="space-y-3 pt-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <Label className="text-[10px] text-muted-foreground uppercase">Font</Label>
                                <Select 
                                  value={caption.fontFamily || config.captionDefaults.fontFamily}
                                  onValueChange={(v) => handleCaptionChange(caption.id, 'fontFamily', v)}
                                >
                                  <SelectTrigger className="h-8 bg-muted border-border text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-muted border-border text-foreground">
                                    {["Arial", "Helvetica", "Times New Roman", "Courier New", "Verdana", "Georgia", "Impact", "Comic Sans MS"].map(f => (
                                      <SelectItem key={f} value={f}>{f}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-[10px] text-muted-foreground uppercase">Size (px)</Label>
                                <Input 
                                  type="number" 
                                  className="h-8 bg-muted border-border text-xs"
                                  value={caption.fontSize || config.captionDefaults.fontSize}
                                  onChange={(e) => handleCaptionChange(caption.id, 'fontSize', parseInt(e.target.value) || 24)}
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <ColorPickerWithAlpha
                                label="Text Color"
                                value={caption.color || config.captionDefaults.color}
                                onChange={(v) => handleCaptionChange(caption.id, 'color', v)}
                                disableAlpha={true}
                              />
                              <ColorPickerWithAlpha
                                label="Background"
                                value={caption.backgroundColor || config.captionDefaults.backgroundColor}
                                onChange={(v) => handleCaptionChange(caption.id, 'backgroundColor', v)}
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-[10px] text-muted-foreground uppercase">Position</Label>
                              <Select 
                                value={caption.position || config.captionDefaults.position}
                                onValueChange={(v) => handleCaptionChange(caption.id, 'position', v)}
                              >
                                <SelectTrigger className="h-8 bg-muted border-border text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-muted border-border text-foreground">
                                  <SelectItem value="top">Top</SelectItem>
                                  <SelectItem value="center">Center</SelectItem>
                                  <SelectItem value="bottom">Bottom</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="p-4 border-t border-border bg-muted flex justify-end gap-3">
          <Button variant="ghost" onClick={handleCancel} className="text-muted-foreground hover:text-foreground hover:bg-card">
            Cancel
          </Button>
          <Button onClick={handleApply} className="bg-primary hover:bg-primary/90 text-white min-w-[100px]">
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
